import styled from "styled-components/macro";
import { format } from "date-fns";
import { ChartPluginsProps, DataDumpAggregate } from "../../types";
import { FORMAT_TIME } from "../../../utils/tier0-utils";
import { HourlyPerformanceChart } from "./HourlyPerformanceChart";
import {
  lightAccentFail50,
  lightAccentInfo70,
  lightAccentSuccess50,
  lightBlueLiveBackground,
} from "../../../utils/tier0-colors";
import { useMeasure } from "react-use";
import { TeamSizeColumn } from "./TeamSizeColumn";
import { SkuRowColumn, BORDER_WIDTH } from "./SkuRowColumn";
import { ONE_MINUTE_OF_MS, formatHoursMinutes } from "utils/timeUtils";
import ErrorBoundary from "components/SimpleComponents/Errors/ErrorBoundary";
import { TIER0_ROW_HEIGHT } from "constants/constants";

/* turn green when output > this % of the target output */
export const TIER0_TARGET_PERCENT = 0.85;

const VerticalTimeline = ({
  hourlyDataDumpAggregates,
  paddingWidth,
  colWidths,
  teamSizeChangesHistory,
  skuCodeChangesHistory,
  hourlyTopDowntimes,
  isCurrentShift,
  scale,
}: {
  hourlyDataDumpAggregates: DataDumpAggregate[];
  paddingWidth: number;
  colWidths: number[];
  teamSizeChangesHistory: ChartPluginsProps["teamSizeChangesHistory"];
  skuCodeChangesHistory: ChartPluginsProps["skuCodeChangesHistory"];
  hourlyTopDowntimes: ChartPluginsProps["hourlyTopDowntimes"];
  isCurrentShift: boolean;
  scale: number;
}) => {
  const [ref, dimensions] = useMeasure<HTMLTableSectionElement>();

  // black target lines indicate targer for the hour,
  // but where do we place the target line horizontally?
  // -> the biggest target OR output determines what is "full width"
  const maxOutputOrPotentialOutput = hourlyDataDumpAggregates.reduce(
    (acc, cur) => Math.max(acc, cur.totalPotentialOutput, cur.netOutput),
    0
  );
  const [refHourlyPerformance, dimensionsHourlyPerformance] =
    useMeasure<HTMLTableCellElement>();
  const [refTotalDowntime, dimensionsTotalDowntime] =
    useMeasure<HTMLTableCellElement>();
  const lastTwoColsWidth =
    dimensionsHourlyPerformance.width +
    dimensionsTotalDowntime.width +
    scale * 45; // ? magic number
  return (
    <Styles
      {...{
        $lastTwoColsWidth: lastTwoColsWidth,
        $paddingWidth: paddingWidth,
        $colWidths: colWidths,
        $scale: scale,
      }}
    >
      <table>
        <colgroup>
          <col className="cols-left" span={5} />
          <col className="cols-right" span={3} />
        </colgroup>
        <ErrorBoundary>
          <TableHead {...{ colWidths, scale }} />
        </ErrorBoundary>
        <ErrorBoundary>
          <tbody ref={ref}>
            {hourlyDataDumpAggregates.map((dataDumpAggregate, idx) => {
              const { ge, lt, netOutput, totalPotentialOutput } =
                dataDumpAggregate;

              const isAboveTarget =
                Math.round(netOutput) >=
                Math.round(totalPotentialOutput * TIER0_TARGET_PERCENT);

              const isFirst = idx === 0;
              const isLast = idx === hourlyDataDumpAggregates.length - 1;

              const isLive =
                !lt || Number(lt) > Date.now() || (isCurrentShift && isLast);

              const numRows = hourlyDataDumpAggregates.length;

              const totalDowntimeHourly =
                hourlyTopDowntimes[idx]?.agg_total_downtime_min *
                ONE_MINUTE_OF_MS;
              const topDowntimeName =
                hourlyTopDowntimes[idx]?.agg_reason || "--";
              // const topDowntimeName =
              //   !topDowntime.tagName && topDowntime.duration === 0 && totalDowntimeHourly > 0
              //     ? "unknown"
              //     : topDowntime.tagName;
              return (
                <tr
                  key={idx}
                  className={isFirst ? `first` : isLast ? `last` : ""}
                >
                  {/* Time */}
                  <td className="vertical-timeline-td time">
                    <ErrorBoundary>
                      <div className="text">
                        {format(Number(ge), FORMAT_TIME)}
                      </div>
                    </ErrorBoundary>
                  </td>
                  {/* since HC and Work Order & Product columns have cells of any arbitrary size, not necessarily conforming to the table grid,
         their <td> cells span the whole table height, then we populate their contents with flexbox elements */}
                  {/* HC */}
                  {idx === 0 && (
                    <td
                      rowSpan={numRows}
                      className="vertical-timeline-td head-count-container"
                    >
                      <ErrorBoundary>
                        <TeamSizeColumn
                          {...{ dimensions, teamSizeChangesHistory }}
                        />
                      </ErrorBoundary>
                    </td>
                  )}
                  {/* Work Order & Product */}
                  {idx === 0 && (
                    <td
                      rowSpan={numRows}
                      className="vertical-timeline-td work-order-container"
                    >
                      <ErrorBoundary>
                        <SkuRowColumn
                          {...{
                            scale,
                            dimensions,
                            skuCodeChangesHistory,
                            isCurrentShift,
                          }}
                        />
                      </ErrorBoundary>
                    </td>
                  )}
                  {/* Target */}
                  {/* TODO is totalPotentialOutput correct, or is "target" something else? */}
                  <td className={`vertical-timeline-td target`}>
                    {Math.round(totalPotentialOutput)}
                  </td>
                  {/* Output */}
                  <td
                    className={`vertical-timeline-td output ${
                      isLive
                        ? "live"
                        : isAboveTarget
                          ? "above-target"
                          : "below-target"
                    }`}
                  >
                    <div>{Math.round(netOutput)}</div>
                  </td>
                  {/* Hourly Performance */}
                  <td ref={refHourlyPerformance} className="hourly-performance">
                    <ErrorBoundary>
                      <HourlyPerformanceChart
                        {...{
                          dataDumpAggregate,
                          isAboveTarget,
                          maxOutputOrPotentialOutput,
                          scale,
                        }}
                      />
                    </ErrorBoundary>
                  </td>
                  {/* Total Downtime + top downtime tag  */}
                  <ErrorBoundary>
                    <td ref={refTotalDowntime} className="total-downtime">
                      <div className="flex-container">
                        <div className="duration">
                          {formatHoursMinutes(totalDowntimeHourly)}
                        </div>
                        <div className="pipe">|</div>
                        <div className="tagName">{topDowntimeName}</div>
                      </div>
                    </td>
                  </ErrorBoundary>
                </tr>
              );
            })}
          </tbody>
        </ErrorBoundary>
      </table>
    </Styles>
  );
};
export default VerticalTimeline;

const Styles = styled.div<{
  $lastTwoColsWidth: number;
  $paddingWidth: number;
  $scale: number;
  $colWidths: number[];
}>`
  padding: 0 ${p => p.$paddingWidth}px ${p => p.$scale * 18}px
    ${p => p.$paddingWidth}px;
  * {
    font-family: "Open Sans";
  }
  table {
    border-spacing: 0px;
  }
  .cols-left {
  }
  .cols-right {
  }
  thead {
    td {
      padding-bottom: ${p => p.$scale * 8}px;
      font-weight: bold;
      font-size: ${p => p.$scale * 18}px;
      line-height: ${p => p.$scale * 26}px;
    }
  }
  tbody {
  }
  tbody tr {
    position: relative;
  }
  .head-count-container,
  .work-order-container {
    vertical-align: top;
  }
  /* first td in each row creates the hour line & half-hour line */
  tbody tr td:first-child {
    &:before,
    &:after {
      content: "";
      position: absolute;
      left: ${p => p.$scale * 5.5}ch;
      right: ${p => p.$lastTwoColsWidth}px;
      height: 0px;
    }
    /* hour line */
    &:before {
      top: 0;
      border-top: ${p => p.$scale * 1}px solid #dbdbdb;
      opacity: 0.5;
    }
    /* half-hour line */
    &:after {
      left: 0;
      top: 50%;
      bottom: 50%;
      border-top: ${p => p.$scale * 1}px dashed #dbdbdb;
      stroke-dasharray: ${p => p.$scale * 2};
      opacity: 0.5;
    }
  }
  .time {
    line-height: ${p => p.$scale * 28}px;
    font-size: ${p => p.$scale * 20}px;
    .text {
      transform: translateY(${p => p.$scale * -7}px);
      padding-right: ${p => p.$scale * 24}px;
    }
  }
  .time,
  .head-count,
  .work-order {
    vertical-align: top;
  }
  .head-count .text,
  .work-order .left .zeroHeightWrapper {
    padding: ${p => p.$scale * 6}px ${p => p.$scale * 12}px 0
      ${p => p.$scale * 12}px;
  }
  .head-count,
  .target,
  .output {
    font-size: ${p => p.$scale * 32}px;
    line-height: ${p => p.$scale * 32}px;
    font-weight: bold;
  }

  .work-order,
  .head-count {
    border: ${p => p.$scale * BORDER_WIDTH}px solid ${lightAccentInfo70};
    border-radius: ${p => p.$scale * 2}px;
    &:last-child {
      margin-bottom: ${p => p.$scale * -3}px;
    }
  }
  .head-count.tiny .text {
    display: flex;
    align-items: center;
  }
  .target,
  .output {
    width: ${p => p.$scale * 118}px;
    padding-left: ${p => p.$scale * 12}px;
    border-radius: ${p => p.$scale * 2}px;
  }
  .target {
    border-left: ${p => p.$scale * 1}px solid ${lightAccentInfo70};
  }
  .output {
    border-right: ${p => p.$scale * 1}px solid ${lightAccentInfo70};
    &.above-target {
      background: ${lightAccentSuccess50};
    }
    &.below-target {
      background: ${lightAccentFail50};
    }
    &.live {
      position: relative;
      &:before {
        content: "";
        position: absolute;
        left: 0px;
        right: ${p => p.$scale * 4}px;
        top: ${p => p.$scale * 4}px;
        bottom: ${p => p.$scale * 4}px;
        background: ${lightBlueLiveBackground};
        border-radius: ${p => p.$scale * 2}px;
      }
      &:after {
        content: "Live";
        position: absolute;
        right: ${p => p.$scale * 17}px;
        top: 0;
        bottom: 0;
        color: ${lightAccentInfo70};
        font-size: ${p => p.$scale * 12}px;
        font-weight: bold;
        display: flex;
        align-items: center;
      }
    }
  }
  tr.first {
    .target,
    .output {
      border-top: ${p => p.$scale * 1}px solid ${lightAccentInfo70};
    }
  }
  tr.last {
    .target,
    .output {
      transform: translateY(${p => p.$scale * 1}px);
      border-bottom: ${p => p.$scale * 1}px solid ${lightAccentInfo70};
    }
  }
  .total-downtime {
    height: ${p => p.$scale * TIER0_ROW_HEIGHT}px;
    box-sizing: border-box;
    padding: ${p => p.$scale * 12}px ${p => p.$scale * 16}px
      ${p => p.$scale * 12}px ${p => p.$scale * (16 + 12)}px;
    .flex-container {
      display: flex;
      align-items: center;
    }
    .tagName,
    .pipe {
      font-size: ${p => p.$scale * 18}px;
    }
    .pipe {
      transform: translateY(${p => p.$scale * -2}px);
      padding: 0 ${p => p.$scale * 6}px 0 ${p => p.$scale * 6}px;
      font-size: ${p => p.$scale * 16}px;
    }
    .tagName {
    }
    .duration {
      flex-shrink: 0;
      width: ${p => p.$scale * 52}px;
      font-size: ${p => p.$scale * 24}px;
      font-weight: bold;
      white-space: nowrap;
      text-align: right;
      transform: translateY(${p => p.$scale * -2}px);
      display: flex;
      justify-content: flex-end;
    }
  }
  .hourly-performance {
    padding: ${p => p.$scale * 3}px 0px ${p => p.$scale * 1}px;
  }
`;
function TableHead({
  colWidths,
  scale,
}: {
  colWidths: number[];
  scale: number;
}) {
  const utcOffsetMin = new Date().getTimezoneOffset();
  const utcOffsetHr = utcOffsetMin / 60;
  // for some reason in UTC-4 we get "+4" instead of "-4"
  const utcOffsetHrActual = -utcOffsetHr;
  return (
    <StyledTHead
      $timezoneAbbrev={`UTC${
        utcOffsetHrActual >= 0 ? "+" : ""
      }${utcOffsetHrActual}`}
      $scale={scale}
    >
      <tr>
        <td className="time" style={{ minWidth: colWidths[0] }}>
          Time
        </td>
        <td style={{ minWidth: colWidths[1] }}>HC</td>
        <td style={{ minWidth: colWidths[2] }}>SKU {"&"} Product</td>
        <td style={{ minWidth: colWidths[3] }}>Target</td>
        <td style={{ minWidth: colWidths[4] }}>Output</td>
        <td
          style={{
            paddingLeft: scale * 16,
            minWidth: colWidths[5],
          }}
        >
          Hourly Performance
        </td>
        <td
          style={{
            paddingLeft: scale * 16,
            minWidth: colWidths[6],
          }}
        >
          Total Downtime{" "}
          <span
            style={{
              fontSize: scale * 12,
              fontWeight: "normal",
            }}
          >
            + top downtime tag
          </span>{" "}
        </td>
      </tr>
    </StyledTHead>
  );
}
const StyledTHead = styled.thead<{ $timezoneAbbrev: string; $scale: number }>`
  .container {
  }
  .time {
    position: relative;
    &:after {
      content: "(${p => p.$timezoneAbbrev})";
      position: absolute;
      left: ${p => p.$scale * 2}px;
      font-size: ${p => p.$scale * 10}px;
      top: ${p => p.$scale * -18}px;
      opacity: 0.5;
    }
  }
`;
